import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import './AdminDashboardPage.css';
import {
  createAdminBill,
  createAdminPromoCode,
  deleteAdminBill,
  getAdminAppointments,
  getAdminBills,
  getAdminContactMessages,
  getAdminOrders,
  getAdminPromoCodes,
  createAdminProduct,
  deleteAdminAppointment,
  deleteAdminOrder,
  deleteAdminProduct,
  deleteAdminPromoCode,
  getAdminSummary,
  getProducts,
  getAdminSalesRecords,
  getAdminExpenses,
  createAdminExpense,
  deleteAdminExpense,
  deleteAdminUser,
  getAdminUsers,
  migrateAdminBillPayments,
  updateAdminAppointment,
  updateAdminBillPayment,
  updateAdminOrderStatus,
  updateAdminProduct,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatNpr } from '../lib/currency';
import { getOptimizedImageUrl } from '../lib/imageUrl';
import { getProductDisplayId } from '../lib/productDisplayId';
import { getOrderAddressLine } from '../lib/addressDisplay';

const initialForm = {
  name: '',
  category: '',
  description: '',
  images: ['', '', ''],
  price: '',
  stock: '',
  featured: false,
};

const initialPromoForm = {
  code: '',
  percentOff: '',
};

const initialBillForm = {
  billingMode: 'offline selling',
  paymentMethod: 'cash',
  customerName: '',
  contactNumber: '',
  billingDate: new Date().toISOString().slice(0, 10),
  billingTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
  shopName: 'Lalitpur Eye Care',
  panNumber: 'PAN-000000000',
  discountAmount: 0,
  paidAmount: 0,
  billedBy: 'Kushal Dhungel',
};

const initialBillItem = {
  productId: '',
  quantity: '1',
  unitPrice: '',
};

const categoryOptions = [
  'men sunglasses',
  'women sunglasses',
  'men frames',
  'women frames',
  'baby sunglasses',
  'baby frames',
  'contact lens',
  'lens solution',
  'power lens',
];

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(String(reader.result || ''));
    };
    reader.onerror = () => {
      reject(new Error('Failed to read image file.'));
    };
    reader.readAsDataURL(file);
  });
}

function formatOrderLocation(order) {
  return getOrderAddressLine(order.shippingAddress);
}

function getOrderCustomerName(order) {
  return order.customer?.fullName || order.customerName || 'N/A';
}

function getOrderCustomerPhone(order) {
  return order.customer?.phone || order.contactNumber || order.phone || 'N/A';
}

function getOrderCustomerEmail(order) {
  return order.customer?.email || order.email || '';
}

function formatOrderItems(order) {
  return (order.items || [])
    .map((item) => `${item.product?.name || 'Product'} x${item.quantity}`)
    .join(', ');
}

function createBillItem() {
  return { ...initialBillItem };
}

function getProductNameById(products, productId) {
  return products.find((product) => product.id === productId)?.name || 'Product';
}

function formatProductLabel(product) {
  return `${product.name} (${getProductDisplayId(product.id)})`;
}

function AdminDashboardPage() {
  const { user, isAuthenticated, logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [billRecords, setBillRecords] = useState([]);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [form, setForm] = useState(initialForm);
  const [promoForm, setPromoForm] = useState(initialPromoForm);
  const [billForm, setBillForm] = useState(initialBillForm);
  const [billItems, setBillItems] = useState([createBillItem()]);
  const [editingProductId, setEditingProductId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [activeModal, setActiveModal] = useState('');
  const [selectedBill, setSelectedBill] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null });
  const [salesPeriod, setSalesPeriod] = useState('day');
  const [salesDay, setSalesDay] = useState(new Date().toISOString().slice(0, 10));
  const [salesMonth, setSalesMonth] = useState(new Date().toISOString().slice(0, 7));
  const [salesRecords, setSalesRecords] = useState(null);
  const [expenseDay, setExpenseDay] = useState(new Date().toISOString().slice(0, 10));
  const [expenses, setExpenses] = useState([]);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [expenseForm, setExpenseForm] = useState({ amount: '', message: '' });
  const [productSearches, setProductSearches] = useState({}); // Store search queries for each bill item

  useEffect(() => {
    let isMounted = true;

    async function loadAdminData() {
      try {
        const [
          nextSummary,
          nextProducts,
          nextOrders,
          nextUsers,
          nextAppointments,
          nextPromoCodes,
          nextContactMessages,
          nextBills,
        ] = await Promise.all([
          getAdminSummary({ userId: user.id }),
          getProducts(),
          getAdminOrders({ userId: user.id, date: '' }),
          getAdminUsers({ userId: user.id }),
          getAdminAppointments({ userId: user.id, date: '' }),
          getAdminPromoCodes({ userId: user.id }),
          getAdminContactMessages({ userId: user.id }),
          getAdminBills({ userId: user.id }),
        ]);

        if (!isMounted) {
          return;
        }

        setSummary(nextSummary);
        setProducts(nextProducts);
        setOrders(nextOrders);
        setUsers(nextUsers);
        setAppointments(nextAppointments);
        setPromoCodes(nextPromoCodes);
        setContactMessages(nextContactMessages);
        setBillRecords(nextBills.bills);
        setError('');
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    if (user?.id) {
      loadAdminData();
    }

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-product-search]')) {
        // Clear all searches to close dropdowns
        setProductSearches({});
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: '/admin' }} />;
  }

  if (!user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  function resetForm() {
    setForm(initialForm);
    setEditingProductId('');
  }

  function resetPromoForm() {
    setPromoForm(initialPromoForm);
  }

  function resetBillForm() {
    setBillForm({
      ...initialBillForm,
      billingDate: new Date().toISOString().slice(0, 10),
      billingTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
    });
    setBillItems([createBillItem()]);
    setProductSearches({}); // Clear all product searches
  }

  function startEdit(product) {
    setEditingProductId(product.id);
    setForm({
      name: product.name,
      category: product.category,
      description: product.description,
      images: [...(product.images || []), '', '', ''].slice(0, 3),
      price: String(product.price),
      stock: String(product.stock),
      featured: Boolean(product.featured),
    });
    setNotice('');
    setError('');
    setActiveModal('add-product');
  }

  async function refreshSummary() {
    const nextSummary = await getAdminSummary({ userId: user.id });
    setSummary(nextSummary);
  }

  async function loadAppointmentsByDate(nextDate) {
    const nextAppointments = await getAdminAppointments({
      userId: user.id,
      date: nextDate,
    });
    setAppointments(nextAppointments);
  }

  // loadOrdersByDate removed (orders filtering handled elsewhere)

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSaving(true);
    setError('');
    setNotice('');

    const payload = {
      ...form,
      images: form.images.filter(Boolean),
      price: Number(form.price),
      stock: Number(form.stock),
    };

    try {
      const savedProduct = editingProductId
        ? await updateAdminProduct({
            userId: user.id,
            productId: editingProductId,
            product: payload,
          })
        : await createAdminProduct({
            userId: user.id,
            product: payload,
          });

      setProducts((current) => {
        if (editingProductId) {
          return current.map((product) =>
            product.id === editingProductId ? savedProduct : product
          );
        }

        return [...current, savedProduct];
      });

      await refreshSummary();
      setNotice(editingProductId ? 'Product updated.' : 'Product created.');
      resetForm();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(productId) {
    setError('');
    setNotice('');

    try {
      await deleteAdminProduct({ userId: user.id, productId });
      setProducts((current) => current.filter((product) => product.id !== productId));
      await refreshSummary();

      if (editingProductId === productId) {
        resetForm();
      }

      setNotice('Product deleted.');
    } catch (deleteError) {
      setError(deleteError.message);
    }
  }

  async function handleDeleteBill(billId) {
    setError('');
    setNotice('');

    try {
      await deleteAdminBill({ userId: user.id, billId });
      setBillRecords((current) => current.filter((bill) => bill.id !== billId));
      setNotice('Bill deleted successfully.');
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete bill.');
    }
  }

  function handlePrintBill(bill) {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bill #${bill.id}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .bill-header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .bill-header h1 {
            margin: 0;
            color: #1e3a8a;
          }
          .bill-info {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 30px;
          }
          .bill-info div {
            margin-bottom: 10px;
          }
          .bill-info strong {
            color: #333;
          }
          .bill-items {
            margin-bottom: 30px;
          }
          .bill-items table {
            width: 100%;
            border-collapse: collapse;
          }
          .bill-items th,
          .bill-items td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          .bill-items th {
            background: #f5f5f5;
            font-weight: bold;
          }
          .bill-totals {
            text-align: right;
            margin-top: 20px;
          }
          .bill-totals div {
            margin: 5px 0;
            font-size: 16px;
          }
          .bill-totals .total {
            font-size: 24px;
            font-weight: bold;
            color: #1e3a8a;
            border-top: 2px solid #333;
            padding-top: 10px;
            margin-top: 20px;
          }
          .bill-footer {
            text-align: center;
            margin-top: 40px;
            color: #666;
            font-size: 14px;
          }
        </style>
      </head>
      <body>
        <div class="bill-header">
          <h1>INVOICE</h1>
          <p>${bill.shopName || 'Lalitpur Eye Care'}</p>
        </div>
        <div class="bill-info">
          <div>
            <strong>Bill Number:</strong> #${bill.billNumber || bill.id}
          </div>
          <div>
            <strong>Date:</strong> ${bill.billingDate || 'N/A'}
          </div>
          ${bill.billingTime ? `<div><strong>Time:</strong> ${bill.billingTime}</div>` : ''}
          <div>
            <strong>Customer:</strong> ${bill.customerName || 'N/A'}
          </div>
          <div>
            <strong>Contact:</strong> ${bill.contactNumber || 'N/A'}
          </div>
          <div>
            <strong>Mode:</strong> ${bill.billingMode || 'N/A'}
          </div>
          <div>
            <strong>Payment:</strong> ${bill.paymentMethod || 'N/A'}
          </div>
          ${bill.billedBy ? `<div><strong>Billed By:</strong> ${bill.billedBy}</div>` : ''}
        </div>
        <div class="bill-items">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${bill.items?.map((item) => `
                <tr>
                  <td>${item.productName || 'Item'}</td>
                  <td>${item.quantity}</td>
                  <td>${formatNpr(item.unitPrice)}</td>
                  <td>${formatNpr(item.lineTotal || (item.unitPrice * item.quantity))}</td>
                </tr>
              `).join('') || '<tr><td colspan="4">No items found</td></tr>'}
            </tbody>
          </table>
        </div>
        <div class="bill-totals">
          <div>Subtotal: ${formatNpr(bill.subtotal || 0)}</div>
          ${bill.totalDiscount > 0 ? `<div>Discount: -${formatNpr(bill.totalDiscount)}</div>` : ''}
          <div class="total">Grand Total: ${formatNpr(bill.grandTotal || 0)}</div>
        </div>
        <div class="bill-footer">
          <p>Thank you for your business!</p>
          <p>${bill.shopName || 'Lalitpur Eye Care'}</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }

  async function handleUpdateOrderStatus(orderId) {
    setError('');
    setNotice('');

    const newStatus = prompt('Enter new status (pending, processing, shipped, delivered, cancelled):');
    if (!newStatus) return;

    try {
      await updateAdminOrderStatus({ userId: user.id, orderId, status: newStatus });
      const nextOrders = await getAdminOrders({ userId: user.id });
      setOrders(nextOrders);
      setNotice('Order status updated successfully.');
    } catch (updateError) {
      setError(updateError.message || 'Failed to update order status.');
    }
  }

  async function handleDeleteOrder(order) {
    setConfirmDialog({
      show: true,
      message: `Delete order #${order.orderNumber || order.id}? This cannot be undone.`,
      onConfirm: async () => {
        setError('');
        setNotice('');

        try {
          await deleteAdminOrder({ userId: user.id, orderId: order.id });
          setOrders((current) => current.filter((currentOrder) => currentOrder.id !== order.id));
          await refreshSummary();
          setNotice('Order deleted successfully.');
        } catch (deleteError) {
          setError(deleteError.message || 'Failed to delete order.');
        }

        setConfirmDialog({ show: false, message: '', onConfirm: null });
      },
    });
  }

  async function handleDeleteUser(customer) {
    setConfirmDialog({
      show: true,
      message: `Delete user ${customer.fullName || customer.email || customer.id}? This cannot be undone.`,
      onConfirm: async () => {
        setError('');
        setNotice('');

        try {
          await deleteAdminUser({ userId: user.id, targetUserId: customer.id });
          setUsers((current) => current.filter((currentUser) => currentUser.id !== customer.id));
          await refreshSummary();
          setNotice('User deleted successfully.');
        } catch (deleteError) {
          setError(deleteError.message || 'Failed to delete user.');
        }

        setConfirmDialog({ show: false, message: '', onConfirm: null });
      },
    });
  }

  async function handleRescheduleAppointment(appointmentId) {
    setError('');
    setNotice('');

    const newDate = prompt('Enter new date (YYYY-MM-DD):');
    const newTime = prompt('Enter new time (HH:MM):');
    if (!newDate || !newTime) return;

    try {
      await updateAdminAppointment({ userId: user.id, appointmentId, date: newDate, time: newTime });
      const nextAppointments = await getAdminAppointments({ userId: user.id });
      setAppointments(nextAppointments.appointments);
      setNotice('Appointment rescheduled successfully.');
    } catch (rescheduleError) {
      setError(rescheduleError.message || 'Failed to reschedule appointment.');
    }
  }

  async function handleCancelAppointment(appointmentId) {
    setConfirmDialog({
      show: true,
      message: 'Are you sure you want to cancel this appointment?',
      onConfirm: async () => {
        setError('');
        setNotice('');

        try {
          await deleteAdminAppointment({ userId: user.id, appointmentId });
          setAppointments((current) => current.filter((appointment) => appointment.id !== appointmentId));
          setNotice('Appointment cancelled successfully.');
        } catch (cancelError) {
          setError(cancelError.message || 'Failed to cancel appointment.');
        }

        setConfirmDialog({ show: false, message: '', onConfirm: null });
      },
    });
  }

  async function handlePromoSubmit(event) {
    event.preventDefault();
    setIsSaving(true);
    setError('');
    setNotice('');

    try {
      const savedPromoCode = await createAdminPromoCode({
        userId: user.id,
        promoCode: {
          code: promoForm.code,
          percentOff: Number(promoForm.percentOff),
        },
      });
      setPromoCodes((current) => [savedPromoCode, ...current]);
      resetPromoForm();
      setNotice('Promo code created.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  }

  async function handlePromoDelete(promoCodeId) {
    setError('');
    setNotice('');

    try {
      await deleteAdminPromoCode({ userId: user.id, promoCodeId });
      setPromoCodes((current) => current.filter((promoCode) => promoCode.id !== promoCodeId));
      setNotice('Promo code removed.');
    } catch (deleteError) {
      setError(deleteError.message);
    }
  }

  function updateBillField(field, value) {
    if (field === 'discountAmount') {
      const parsed = Number(value);
      const normalized = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
      setBillForm((current) => ({ ...current, discountAmount: normalized }));
      return;
    }

    if (field === 'paidAmount') {
      const parsed = Number(value);
      const normalized = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
      setBillForm((current) => ({ ...current, paidAmount: normalized }));
      return;
    }

    setBillForm((current) => ({ ...current, [field]: value }));
  }

  function updateBillItem(index, field, value) {
    setBillItems((current) =>
      current.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        const nextItem = {
          ...item,
          [field]: value,
        };

        if (field === 'productId') {
          const matchedProduct = products.find((product) => product.id === value);
          nextItem.unitPrice = matchedProduct ? String(products.find((product) => product.id === value)?.price || '') : item.unitPrice;
        }

        return nextItem;
      })
    );
  }

  function updateProductSearch(index, searchQuery) {
    setProductSearches((current) => ({
      ...current,
      [index]: searchQuery,
    }));
  }

  function getFilteredProducts(searchQuery) {
    if (!searchQuery || searchQuery.trim() === '') {
      return products;
    }

    const query = searchQuery.toLowerCase().trim();
    return products.filter((product) => {
      const productId = getProductDisplayId(product.id).toLowerCase();
      const productName = product.name.toLowerCase();
      return productId.includes(query) || productName.includes(query);
    });
  }

  function handleProductSelect(index, productId) {
    updateBillItem(index, 'productId', productId);
    updateProductSearch(index, ''); // Clear search after selection
  }

  function addBillItem() {
    setBillItems((current) => [...current, createBillItem()]);
    // Clear search for the new item
    const newIndex = billItems.length;
    setProductSearches((current) => ({
      ...current,
      [newIndex]: '',
    }));
  }

  function removeBillItem(index) {
    setBillItems((current) => {
      if (current.length === 1) {
        return current;
      }

      return current.filter((_item, itemIndex) => itemIndex !== index);
    });
  }

  function printBill() {
    const normalizedItems = previewBillItems;

    if (!normalizedItems.length) {
      setError('Add at least one bill item with a product and price.');
      return;
    }

    const subtotal = normalizedItems.reduce((total, item) => total + item.quantity * item.unitPrice, 0);
    const totalDiscount = Math.min(subtotal, Math.max(0, Number(billForm.discountAmount) || 0));
    const grandTotal = subtotal - totalDiscount;
    const billNumber = `BILL-${Date.now()}`;
    const currentTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    const currentDate = new Date().toISOString().slice(0, 10);
    const printWindow = window.open('', '_blank', 'width=420,height=900');

    if (!printWindow) {
      setError('Allow popups to print the bill.');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>${billNumber}</title>
          <style>
            @page { size: 80mm auto; margin: 4mm; }
            html, body { width: 80mm; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; color: #111827; font-size: 11px; line-height: 1.35; }
            .receipt { width: 72mm; padding: 2mm 0; }
            .center { text-align: center; }
            h1 { margin: 0 0 4px; font-size: 18px; line-height: 1.15; }
            p { margin: 2px 0; }
            .section { margin-top: 10px; }
            .divider { border-top: 1px dashed #9ca3af; margin: 8px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 6px; }
            th, td { padding: 4px 0; text-align: left; vertical-align: top; }
            th { font-size: 10px; border-bottom: 1px solid #d1d5db; }
            td:last-child, th:last-child { text-align: right; }
            .totals { margin-top: 8px; }
            .totals div { display: flex; justify-content: space-between; padding: 2px 0; }
            .totals strong { font-size: 13px; }
            .muted { color: #4b5563; }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="center">
              <h1>${billForm.shopName}</h1>
              <p>PAN: ${billForm.panNumber}</p>
              <p>Bill No: ${billNumber}</p>
              <p>Date: ${currentDate}</p>
              <p>Time: ${currentTime}</p>
              <p>Mode: ${billForm.billingMode === 'offline selling' ? 'Offline Selling' : 'Online Selling'}</p>
              <p>Payment: ${billForm.paymentMethod === 'online' ? 'Online' : 'Cash'}</p>
              ${billForm.billedBy ? `<p>Billed By: ${billForm.billedBy}</p>` : ''}
            </div>
            <div class="divider"></div>
            <div class="section">
              <p><strong>Customer:</strong> ${billForm.customerName}</p>
              <p><strong>Contact:</strong> ${billForm.contactNumber}</p>
            </div>
            <div class="divider"></div>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>${normalizedItems
              .map(
                (item) => `
                  <tr>
                    <td>
                      <div>${item.productName}</div>
                      <div class="muted">${formatNpr(item.unitPrice)} x ${item.quantity}</div>
                    </td>
                    <td>${item.quantity}</td>
                    <td>${formatNpr(item.quantity * item.unitPrice)}</td>
                  </tr>
                `
              )
              .join('')}</tbody>
          </table>
          <div class="totals">
            <div><span>Subtotal</span><span>${formatNpr(subtotal)}</span></div>
            ${totalDiscount > 0 ? `<div><span>Discount</span><span>- ${formatNpr(totalDiscount)}</span></div>` : ''}
            <div><strong>Grand Total</strong><strong>${formatNpr(grandTotal)}</strong></div>
          </div>
          <div class="divider"></div>
          <div class="center">
            <p>Thank you</p>
          </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  async function handleSaveBill() {
    if (!previewBillItems.length) {
      setError('Add at least one bill item before saving.');
      return;
    }

    if (!String(billForm.customerName || '').trim() || !String(billForm.contactNumber || '').trim()) {
      setError('Customer name and contact number are required.');
      return;
    }

    setIsSaving(true);
    setError('');
    setNotice('');

    try {
      const billPayload = {
        ...billForm,
        billingDate: new Date().toISOString().slice(0, 10),
        billingTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        discountAmount: Math.max(0, Number(billForm.discountAmount) || 0),
        paidAmount: Math.max(0, Number(billForm.paidAmount) || 0),
        items: previewBillItems.map((item) => ({
          productId: item.productId || '',
          productName: item.productName || 'Custom Item',
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          lineTotal: item.quantity * item.unitPrice,
        })),
      };
      const response = await createAdminBill({
        userId: user.id,
        bill: billPayload,
      });
      setBillRecords((current) => [response.bill, ...current]);
      const nextBills = await getAdminBills({ userId: user.id });
      setBillRecords(nextBills.bills);
      setNotice('Bill saved for future record.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  }

  const previewBillItems = billItems
    .map((item) => {
      const quantity = Math.max(1, Number(item.quantity) || 1);
      const unitPrice = Number(item.unitPrice) || 0;
      const lineTotal = quantity * unitPrice;

      return {
        ...item,
        quantity,
        unitPrice,
        lineTotal,
        productName: getProductNameById(products, item.productId),
      };
    })
    .filter((item) => item.productId || item.unitPrice > 0);

  const previewSubtotal = previewBillItems.reduce(
    (total, item) => total + item.quantity * item.unitPrice,
    0
  );
  const previewDiscount = Math.min(previewSubtotal, Math.max(0, Number(billForm.discountAmount) || 0));
  const previewGrandTotal = previewSubtotal - previewDiscount;
  const previewPaidAmount = Math.min(
    Math.max(0, Number(billForm.paidAmount) || 0),
    Math.max(0, previewGrandTotal)
  );
  const previewRemainingAmount = Math.max(0, previewGrandTotal - previewPaidAmount);

  async function handleImageUpload(index, file) {
    if (!file) {
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setForm((current) => {
        const images = [...current.images];
        images[index] = dataUrl;
        return { ...current, images };
      });
    } catch (uploadError) {
      setError(uploadError.message);
    }
  }

  function handleRemoveImage(index) {
    setForm((current) => {
      const images = [...current.images];
      images[index] = '';
      return { ...current, images };
    });
  }

  const adminActions = [
    { id: 'add-product', title: editingProductId ? 'Edit Product' : 'Add Product', meta: 'Create or update catalog items fast.' },
    { id: 'view-products', title: 'View Products', meta: `${products.length} products in catalog.` },
    { id: 'billing', title: 'Create Bill', meta: 'Prepare a printable invoice with PAN and discounts.' },
    { id: 'saved-bills', title: 'Saved Bills', meta: `${billRecords.length} saved bills in record.` },
    { id: 'promo-codes', title: 'Promo Codes', meta: `${promoCodes.length} discount codes available.` },
    { id: 'sales-records', title: 'Sales Records', meta: 'Track daily or monthly total sales.' },
    { id: 'expenses', title: 'Expenses', meta: 'Log daily expenses with notes.' },
    { id: 'contact-messages', title: 'Contact Messages', meta: `${contactMessages.length} customer messages received.` },
    { id: 'view-users', title: 'View Users', meta: `${users.length || summary?.metrics.userCount || 0} users in customer records.` },
    { id: 'view-orders', title: 'See Orders', meta: `${orders.length} recent customer orders.` },
    { id: 'view-appointments', title: 'See Appointments', meta: 'Filter bookings by appointment date.' },
    { id: 'migrate-bills', title: 'Migrate Bills', meta: 'Fix payment tracking for existing bills.' },
  ];

  async function loadSalesRecords({ period, day, month }) {
    const response = await getAdminSalesRecords({
      userId: user.id,
      period,
      day,
      month,
    });
    setSalesRecords(response);
  }

  async function loadExpenses(day) {
    const response = await getAdminExpenses({ userId: user.id, day });
    setExpenses(response.expenses || []);
    setExpenseTotal(Number(response.totalExpenses || 0));
  }

  async function handleCreateExpense(event) {
    event.preventDefault();
    setIsSaving(true);
    setError('');
    setNotice('');

    try {
      await createAdminExpense({
        userId: user.id,
        expense: {
          expenseDate: expenseDay,
          amount: Number(expenseForm.amount),
          message: expenseForm.message,
        },
      });

      setExpenseForm({ amount: '', message: '' });
      await loadExpenses(expenseDay);
      setNotice('Expense saved.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  }

  function handleDeleteExpense(expenseId) {
    setConfirmDialog({
      show: true,
      message: 'Are you sure you want to delete this expense?',
      onConfirm: async () => {
        setError('');
        setNotice('');

        try {
          await deleteAdminExpense({ userId: user.id, expenseId });
          setExpenses((current) => {
            const nextExpenses = current.filter((expense) => expense.id !== expenseId);
            setExpenseTotal(
              Number(nextExpenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0).toFixed(2))
            );
            return nextExpenses;
          });
          setNotice('Expense deleted.');
        } catch (deleteError) {
          setError(deleteError.message || 'Failed to delete expense.');
        }

        setConfirmDialog({ show: false, message: '', onConfirm: null });
      },
    });
  }

  async function handleMigrateBills() {
    setIsSaving(true);
    setError('');
    setNotice('');

    try {
      const result = await migrateAdminBillPayments({ userId: user.id });
      setNotice(`Successfully migrated ${result.migratedCount} bills.`);
      
      // Refresh bills data
      const nextBills = await getAdminBills({ userId: user.id });
      setBillRecords(nextBills.bills);
      
    } catch (migrationError) {
      setError(migrationError.message);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="admin-sidebar__header">
          <div className="admin-sidebar__logo">
            <img src="/Logo 1.png" alt="Logo" className="admin-sidebar__logo-image" />
            <span className="admin-sidebar__logo-text">Admin</span>
          </div>
        </div>
        
        <nav className="admin-sidebar__nav">
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === '' ? 'is-active' : ''}`}
            onClick={() => setActiveModal('')}
          >
            📊 Dashboard
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'add-product' ? 'is-active' : ''}`}
            onClick={() => {
              setActiveModal('add-product');
              resetForm();
            }}
          >
            ➕ Add Product
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'view-products' ? 'is-active' : ''}`}
            onClick={() => setActiveModal('view-products')}
          >
            📦 Products
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'billing' ? 'is-active' : ''}`}
            onClick={() => {
              setActiveModal('billing');
              resetBillForm();
            }}
          >
            💳 Billing
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'saved-bills' ? 'is-active' : ''}`}
            onClick={() => setActiveModal('saved-bills')}
          >
            📋 Saved Bills
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'view-orders' ? 'is-active' : ''}`}
            onClick={() => setActiveModal('view-orders')}
          >
            🛒 Orders
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'view-appointments' ? 'is-active' : ''}`}
            onClick={() => setActiveModal('view-appointments')}
          >
            📅 Appointments
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'promo-codes' ? 'is-active' : ''}`}
            onClick={() => setActiveModal('promo-codes')}
          >
            🏷️ Promo Codes
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'sales-records' ? 'is-active' : ''}`}
            onClick={() => {
              setActiveModal('sales-records');
              void loadSalesRecords({
                period: salesPeriod,
                day: salesDay,
                month: salesMonth,
              });
            }}
          >
            📈 Sales Records
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'expenses' ? 'is-active' : ''}`}
            onClick={() => {
              setActiveModal('expenses');
              void loadExpenses(expenseDay);
            }}
          >
            🧾 Expenses
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'contact-messages' ? 'is-active' : ''}`}
            onClick={() => setActiveModal('contact-messages')}
          >
            📧 Messages
          </button>
          <button
            type="button"
            className={`admin-sidebar__link ${activeModal === 'view-users' ? 'is-active' : ''}`}
            onClick={() => setActiveModal('view-users')}
          >
            👥 Users
          </button>
        </nav>
        
        <div className="admin-sidebar__footer">
          <button
            type="button"
            className="admin-sidebar__logout"
            onClick={logout}
          >
            🚪 Logout
          </button>
        </div>
      </aside>

      <main className="admin-content">
        <div className="admin-content__header">
          <h1 className="admin-content__title">
            {activeModal === '' && 'Dashboard'}
            {activeModal === 'add-product' && (editingProductId ? 'Edit Product' : 'Add Product')}
            {activeModal === 'view-products' && 'Products'}
            {activeModal === 'billing' && 'Billing'}
            {activeModal === 'saved-bills' && 'Saved Bills'}
            {activeModal === 'view-orders' && 'Orders'}
            {activeModal === 'view-appointments' && 'Appointments'}
            {activeModal === 'promo-codes' && 'Promo Codes'}
            {activeModal === 'sales-records' && 'Sales Records'}
            {activeModal === 'expenses' && 'Expenses'}
            {activeModal === 'contact-messages' && 'Contact Messages'}
            {activeModal === 'view-users' && 'Users'}
            {activeModal === 'migrate-bills' && 'Migrate Bills'}
          </h1>
        </div>

        <div className="admin-content__body">
          {isLoading ? <p className="admin-loading">Loading...</p> : null}
          {error ? <p className="admin-error">{error}</p> : null}
          {notice ? <p className="admin-notice">{notice}</p> : null}

          {activeModal === '' && summary ? (
            <div className="admin-dashboard-view">
              <div className="admin-metrics">
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon">📦</div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__label">Products</span>
                    <strong className="admin-metric-card__value">{summary.metrics.productCount}</strong>
                  </div>
                </div>
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon">👥</div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__label">Users</span>
                    <strong className="admin-metric-card__value">{summary.metrics.userCount}</strong>
                  </div>
                </div>
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon">🛒</div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__label">Orders</span>
                    <strong className="admin-metric-card__value">{summary.metrics.orderCount}</strong>
                  </div>
                </div>
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon">📅</div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__label">Appointments</span>
                    <strong className="admin-metric-card__value">{summary.metrics.appointmentCount}</strong>
                  </div>
                </div>
              </div>

              <div className="admin-quick-actions">
                <h2 className="admin-section-title">Quick Actions</h2>
                <div className="admin-action-grid">
                  {adminActions.slice(0, 4).map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      className="admin-action-card"
                      onClick={() => setActiveModal(action.id)}
                    >
                      <span className="admin-action-card__icon">
                        {action.id === 'add-product' && '➕'}
                        {action.id === 'view-products' && '📦'}
                        {action.id === 'billing' && '💳'}
                        {action.id === 'saved-bills' && '📋'}
                      </span>
                      <div className="admin-action-card__content">
                        <strong>{action.title}</strong>
                        <span>{action.meta}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {activeModal === 'add-product' ? (
            <div className="admin-form-container">
              <form className="admin-form" onSubmit={handleSubmit}>
                <div className="admin-form__section">
                  <h3 className="admin-form__section-title">Basic Information</h3>
                  <div className="admin-form__row">
                    <label className="admin-form__field">
                      <span>Product Name</span>
                      <input
                        required
                        type="text"
                        placeholder="Enter product name"
                        value={form.name}
                        onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                      />
                    </label>
                    <label className="admin-form__field">
                      <span>Category</span>
                      <select
                        required
                        value={form.category}
                        onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                      >
                        <option value="">Select category</option>
                        {categoryOptions.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label className="admin-form__field">
                    <span>Description</span>
                    <textarea
                      required
                      rows="4"
                      placeholder="Product description"
                      value={form.description}
                      onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                    />
                  </label>
                </div>

                <div className="admin-form__section">
                  <h3 className="admin-form__section-title">Pricing & Inventory</h3>
                  <div className="admin-form__row">
                    <label className="admin-form__field">
                      <span>Price (NPR)</span>
                      <input
                        required
                        min="0.01"
                        step="0.01"
                        type="number"
                        value={form.price}
                        onChange={(event) => setForm((current) => ({ ...current, price: event.target.value }))}
                      />
                    </label>
                    <label className="admin-form__field">
                      <span>Stock</span>
                      <input
                        required
                        min="0"
                        step="1"
                        type="number"
                        value={form.stock}
                        onChange={(event) => setForm((current) => ({ ...current, stock: event.target.value }))}
                      />
                    </label>
                  </div>
                  <label className="admin-form__checkbox">
                    <input
                      type="checkbox"
                      checked={form.featured}
                      onChange={(event) => setForm((current) => ({ ...current, featured: event.target.checked }))}
                    />
                    <span>Featured Product</span>
                  </label>
                </div>

                <div className="admin-form__section">
                  <h3 className="admin-form__section-title">Images (Up to 3)</h3>
                  <div className="admin-image-grid">
                    {form.images.map((image, index) => (
                      <div key={index} className="admin-image-slot">
                        <div className="admin-image-slot__preview">
                          {image ? (
                            <img src={image} alt={`Preview ${index + 1}`} />
                          ) : (
                            <span>No Image</span>
                          )}
                        </div>
                        <input
                          accept="image/*"
                          type="file"
                          onChange={(event) => void handleImageUpload(index, event.target.files?.[0])}
                        />
                        {image && (
                          <button
                            type="button"
                            className="admin-form__ghost"
                            onClick={() => handleRemoveImage(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="admin-form__actions">
                  <button
                    type="button"
                    className="admin-form__cancel"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="admin-form__submit"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : (editingProductId ? 'Update Product' : 'Add Product')}
                  </button>
                </div>
              </form>
            </div>
          ) : null}

          {activeModal === 'view-products' ? (
            <div className="admin-products-list">
              {products.map((product) => (
                <div key={product.id} className="admin-product-card">
                  <div className="admin-product-card__image">
                    <img
                      src={getOptimizedImageUrl(product.image, 400)}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="admin-product-card__content">
                    <h3>{product.name}</h3>
                    <p className="admin-product-card__id">ID: {getProductDisplayId(product.id)}</p>
                    <p className="admin-product-card__category">{product.category}</p>
                    <p className="admin-product-card__price">{formatNpr(product.price)}</p>
                  </div>
                  <div className="admin-product-card__actions">
                    <button
                      type="button"
                      className="admin-form__ghost"
                      onClick={() => startEdit(product)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="admin-form__ghost admin-form__ghost--danger"
                      onClick={() => void handleDelete(product.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {activeModal === 'billing' ? (
            <div className="admin-billing-container">
              <div className="admin-billing-form">
                <h3 className="admin-form__section-title">Billing Details</h3>
                <div className="admin-form__row">
                  <label className="admin-form__field">
                    <span>Customer Name</span>
                    <input
                      required
                      type="text"
                      value={billForm.customerName}
                      onChange={(event) => updateBillField('customerName', event.target.value)}
                    />
                  </label>
                  <label className="admin-form__field">
                    <span>Contact Number</span>
                    <input
                      required
                      type="text"
                      value={billForm.contactNumber}
                      onChange={(event) => updateBillField('contactNumber', event.target.value)}
                    />
                  </label>
                </div>
                <div className="admin-form__row">
                  <label className="admin-form__field">
                    <span>Billing Mode</span>
                    <select
                      value={billForm.billingMode}
                      onChange={(event) => updateBillField('billingMode', event.target.value)}
                    >
                      <option value="online selling">Online Selling</option>
                      <option value="offline selling">Offline Selling</option>
                    </select>
                  </label>
                  <label className="admin-form__field">
                    <span>Payment Method</span>
                    <select
                      value={billForm.paymentMethod}
                      onChange={(event) => updateBillField('paymentMethod', event.target.value)}
                    >
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                    </select>
                  </label>
                </div>
                <label className="admin-form__label">
                  <span>Billed By</span>
                  <select
                    value={billForm.billedBy}
                    onChange={(event) => updateBillField('billedBy', event.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="">Select Billed By</option>
                    <option value="Satya Sharma">Satya Sharma</option>
                    <option value="Kushal Dhungel">Kushal Dhungel</option>
                  </select>
                </label>
              </div>

              <div className="admin-billing-items">
                <h3 className="admin-form__section-title">Bill Items</h3>
                {billItems.map((item, index) => (
                  <div key={index} className="admin-bill-item">
                    <div className="admin-form__row">
                      <label className="admin-form__field">
                        <span>Product</span>
                        <div style={{ position: 'relative' }} data-product-search>
                          <input
                            type="text"
                            value={productSearches[index] || ''}
                            onChange={(event) => updateProductSearch(index, event.target.value)}
                            placeholder="Search by ID or name..."
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                          {productSearches[index] && (
                            <div style={{ 
                              position: 'absolute', 
                              top: '100%', 
                              left: 0, 
                              right: 0, 
                              backgroundColor: 'white', 
                              border: '1px solid #ddd', 
                              borderTop: 'none', 
                              borderRadius: '0 0 4px 4px',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              zIndex: 1000
                            }}>
                              {getFilteredProducts(productSearches[index]).map((product) => (
                                <div
                                  key={product.id}
                                  onClick={() => handleProductSelect(index, product.id)}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #eee',
                                    fontSize: '0.9em'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                >
                                  <div style={{ fontWeight: 'bold' }}>{formatProductLabel(product)}</div>
                                  <div style={{ fontSize: '0.8em', color: '#666' }}>{product.name}</div>
                                  <div style={{ fontSize: '0.8em', color: '#007bff' }}>Rs. {product.price}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {item.productId && (
                          <div style={{ marginTop: '4px', fontSize: '0.8em', color: '#28a745' }}>
                            Selected: {formatProductLabel(products.find(p => p.id === item.productId) || { id: item.productId, name: 'Unknown' })}
                          </div>
                        )}
                      </label>
                      <label className="admin-form__field">
                        <span>Quantity</span>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(event) => updateBillItem(index, 'quantity', event.target.value)}
                        />
                      </label>
                    </div>
                    <div className="admin-form__row">
                      <label className="admin-form__field">
                        <span>Unit Price</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(event) => updateBillItem(index, 'unitPrice', event.target.value)}
                        />
                      </label>
                    </div>
                    <button
                      type="button"
                      className="admin-form__ghost admin-form__ghost--danger"
                      onClick={() => removeBillItem(index)}
                    >
                      Remove Item
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="admin-form__ghost"
                  onClick={addBillItem}
                >
                  + Add Item
                </button>
              </div>

              <div className="admin-billing-summary">
                <div className="admin-billing-summary__row">
                  <span>Subtotal:</span>
                  <strong>{formatNpr(previewSubtotal)}</strong>
                </div>
                <div className="admin-billing-summary__row">
                  <span>Discount Rs.:</span>
                  <strong>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={billForm.discountAmount}
                      onChange={(event) => updateBillField('discountAmount', event.target.value)}
                      style={{
                        width: 90,
                        padding: '6px 8px',
                        borderRadius: 10,
                        border: '1px solid #d1d5db',
                        textAlign: 'right',
                      }}
                    />
                  </strong>
                </div>
                {previewDiscount > 0 && (
                  <div className="admin-billing-summary__row">
                    <span>Discount amount:</span>
                    <strong>- {formatNpr(previewDiscount)}</strong>
                  </div>
                )}
                <div className="admin-billing-summary__row">
                  <span>Paid (Advance):</span>
                  <strong>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={billForm.paidAmount}
                      onChange={(event) => updateBillField('paidAmount', event.target.value)}
                      style={{
                        width: 120,
                        padding: '6px 8px',
                        borderRadius: 10,
                        border: '1px solid #d1d5db',
                        textAlign: 'right',
                      }}
                    />
                  </strong>
                </div>
                {previewRemainingAmount > 0 ? (
                  <div className="admin-billing-summary__row">
                    <span>Remaining:</span>
                    <strong>{formatNpr(previewRemainingAmount)}</strong>
                  </div>
                ) : (
                  <div className="admin-billing-summary__row">
                    <span>Payment:</span>
                    <strong>Paid</strong>
                  </div>
                )}
                <div className="admin-billing-summary__row admin-billing-summary__row--total">
                  <span>Grand Total:</span>
                  <strong>{formatNpr(previewGrandTotal)}</strong>
                </div>
              </div>

              <div className="admin-form__actions">
                <button
                  type="button"
                  className="admin-form__submit"
                  onClick={printBill}
                >
                  🖨️ Print Bill
                </button>
                <button
                  type="button"
                  className="admin-form__submit"
                  onClick={handleSaveBill}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : '💾 Save Bill'}
                </button>
              </div>
            </div>
          ) : null}

          {activeModal === 'saved-bills' ? (
            <div className="admin-bills-list">
              {billRecords.map((bill) => (
                <div key={bill.id} className="admin-bill-card">
                  <div className="admin-bill-card__header">
                    <div className="admin-bill-card__id">
                      <strong>Bill #{bill.billNumber || bill.id}</strong>
                      <span>{bill.billingDate} {bill.billingTime ? `at ${bill.billingTime}` : ''}</span>
                    </div>
                    <span className={`admin-record-badge ${bill.isFullyPaid ? 'admin-record-badge--paid' : 'admin-record-badge--due'}`}>
                      {bill.isFullyPaid ? 'Paid' : 'Due'}
                    </span>
                  </div>
                  <div className="admin-bill-card__content">
                    <div className="admin-record-info-grid">
                      <p><strong>Customer</strong><span>{bill.customerName || 'N/A'}</span></p>
                      <p><strong>Contact</strong><span>{bill.contactNumber || 'N/A'}</span></p>
                      <p><strong>Mode</strong><span>{bill.billingMode || 'N/A'}</span></p>
                      <p><strong>Payment</strong><span>{bill.paymentMethod || 'N/A'}</span></p>
                      <p><strong>Items</strong><span>{bill.items?.length || 0}</span></p>
                      {bill.totalDiscount > 0 && (
                        <p><strong>Discount</strong><span>-{formatNpr(bill.totalDiscount)}</span></p>
                      )}
                    </div>
                    <div className="admin-record-money-grid">
                      <div>
                        <span>Grand Total</span>
                        <strong>{formatNpr(bill.grandTotal || 0)}</strong>
                      </div>
                      <div>
                        <span>Total Paid</span>
                        <strong>{formatNpr(bill.paidAmount || 0)}</strong>
                      </div>
                      <div>
                        <span>Remaining</span>
                        <strong>{formatNpr(bill.remainingAmount || 0)}</strong>
                      </div>
                    </div>
                    <div className="admin-record-payment-split">
                      <span>Payment Split</span>
                      <div>
                        {bill.onlinePaidAmount > 0 && (
                          <p>Online: {formatNpr(bill.onlinePaidAmount)}</p>
                        )}
                        {bill.cashPaidAmount > 0 && (
                          <p>Cash: {formatNpr(bill.cashPaidAmount)}</p>
                        )}
                        {!bill.onlinePaidAmount && !bill.cashPaidAmount && (
                          <p>No payment yet</p>
                        )}
                      </div>
                    </div>
                    <div className="admin-bill-card__remaining-editor">
                      <strong>Remaining:</strong>
                      <span id={`remaining-amount-${bill.id}`} className="admin-bill-card__remaining-value">
                        {formatNpr(bill.remainingAmount || 0)}
                      </span>
                      <button
                        type="button"
                        className="admin-form__ghost"
                        aria-label="Edit remaining amount"
                        onClick={() => {
                          const remainingSpan = document.getElementById(`remaining-amount-${bill.id}`);
                          const currentRemaining = bill.remainingAmount || 0;
                          const container = remainingSpan.parentNode;
                          
                          // Create payment method dropdown
                          const methodSelect = document.createElement('select');
                          methodSelect.style.marginRight = '8px';
                          methodSelect.style.padding = '2px 4px';
                          methodSelect.style.fontSize = '0.8em';
                          methodSelect.style.border = '1px solid #007bff';
                          methodSelect.style.borderRadius = '3px';
                          methodSelect.innerHTML = `
                            <option value="cash">💵 Cash</option>
                            <option value="online">🌐 Online</option>
                          `;
                          methodSelect.value = bill.paymentMethod || 'cash'; // Default to current payment method
                          
                          // Create input field
                          const input = document.createElement('input');
                          input.type = 'number';
                          input.value = currentRemaining;
                          input.style.width = '100px';
                          input.style.padding = '2px 4px';
                          input.style.fontSize = '0.9em';
                          input.style.border = '1px solid #007bff';
                          input.style.borderRadius = '3px';
                          
                          // Create update button
                          const updateBtn = document.createElement('button');
                          updateBtn.textContent = 'Update';
                          updateBtn.style.padding = '2px 8px';
                          updateBtn.style.fontSize = '0.8em';
                          updateBtn.style.backgroundColor = '#28a745';
                          updateBtn.style.color = 'white';
                          updateBtn.style.border = 'none';
                          updateBtn.style.borderRadius = '3px';
                          updateBtn.style.marginLeft = '4px';
                          updateBtn.style.cursor = 'pointer';
                          
                          // Create container for the new elements
                          const editContainer = document.createElement('div');
                          editContainer.style.display = 'flex';
                          editContainer.style.alignItems = 'center';
                          editContainer.appendChild(methodSelect);
                          editContainer.appendChild(input);
                          editContainer.appendChild(updateBtn);
                          
                          // Replace span with edit container
                          container.replaceChild(editContainer, remainingSpan);
                          input.focus();
                          input.select();
                          
                          // Handle save on button click or Enter
                          const saveRemaining = async () => {
                            const newRemaining = Number(input.value) || 0;
                            const selectedPaymentMethod = methodSelect.value;
                            
                            if (newRemaining < 0) {
                              setError('Remaining amount cannot be negative.');
                              container.replaceChild(remainingSpan, editContainer);
                              return;
                            }
                            
                            if (newRemaining > bill.grandTotal) {
                              setError(`Remaining amount cannot exceed total of ${formatNpr(bill.grandTotal)}`);
                              container.replaceChild(remainingSpan, editContainer);
                              return;
                            }
                            
                            try {
                              const newPaidAmount = bill.grandTotal - newRemaining;
                              const response = await updateAdminBillPayment({
                                userId: user.id,
                                billId: bill.id,
                                paidAmount: newPaidAmount,
                                paymentMethod: selectedPaymentMethod, // Use selected payment method
                              });
                              
                              setBillRecords((current) =>
                                current.map((currentBill) => (currentBill.id === bill.id ? response.bill : currentBill))
                              );
                              
                              setNotice('Remaining amount updated successfully.');
                              setError('');
                              // Restore original display
                              container.replaceChild(remainingSpan, editContainer);
                            } catch (updateError) {
                              setError(updateError.message);
                              container.replaceChild(remainingSpan, editContainer);
                            }
                          };
                          
                          // Add event listeners
                          updateBtn.addEventListener('click', saveRemaining);
                          input.addEventListener('keypress', (e) => {
                            if (e.key === 'Enter') {
                              saveRemaining();
                            }
                          });
                          
                          // Handle cancel on Escape
                          input.addEventListener('keydown', (e) => {
                            if (e.key === 'Escape') {
                              container.replaceChild(remainingSpan, editContainer);
                            }
                          });
                        }}
                      >
                        ✏️
                      </button>
                    </div>
                    
                    {!bill.isFullyPaid && bill.remainingAmount > 0 && (
                      <div className="admin-record-payment-box">
                        <p>Update Payment</p>
                        <div className="admin-record-payment-controls">
                          <div>
                            <select
                              id={`payment-method-select-${bill.id}`}
                              defaultValue="cash"
                            >
                              <option value="cash">Cash</option>
                              <option value="online">Online</option>
                            </select>
                          </div>
                          <div>
                            <input
                              type="number"
                              placeholder="Amount"
                              id={`payment-amount-input-${bill.id}`}
                              min="0"
                              max={bill.remainingAmount}
                              step="0.01"
                            />
                          </div>
                          <button
                            type="button"
                            className="admin-form__ghost"
                            onClick={() => {
                              const methodSelect = document.getElementById(`payment-method-select-${bill.id}`);
                              const amountInput = document.getElementById(`payment-amount-input-${bill.id}`);
                              const paymentMethod = methodSelect.value;
                              const amount = Number(amountInput.value) || 0;
                              
                              if (amount < 0) {
                                setError('Payment amount cannot be negative.');
                                return;
                              }
                              
                              if (amount > bill.remainingAmount) {
                                setError(`Payment amount cannot exceed remaining amount of ${formatNpr(bill.remainingAmount)}`);
                                return;
                              }
                              
                              void (async () => {
                                try {
                                  const totalPaidAmount = (bill.paidAmount || 0) + amount;
                                  const response = await updateAdminBillPayment({
                                    userId: user.id,
                                    billId: bill.id,
                                    paymentMethod,
                                    paidAmount: totalPaidAmount,
                                  });
                                  setBillRecords((current) =>
                                    current.map((currentBill) => (currentBill.id === bill.id ? response.bill : currentBill))
                                  );
                                  setNotice('Payment updated successfully.');
                                  setError('');
                                  // Clear inputs
                                  amountInput.value = '';
                                } catch (updateError) {
                                  setError(updateError.message);
                                }
                              })();
                            }}
                          >
                            Update
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <label className="admin-form__checkbox admin-record-paid-toggle">
                      <input
                        type="checkbox"
                        checked={Boolean(bill.isFullyPaid)}
                        onChange={(event) => {
                          const isFullyPaid = event.target.checked;
                          void (async () => {
                            try {
                              const response = await updateAdminBillPayment({
                                userId: user.id,
                                billId: bill.id,
                                isFullyPaid,
                              });
                              setBillRecords((current) =>
                                current.map((currentBill) => (currentBill.id === bill.id ? response.bill : currentBill))
                              );
                              setNotice(isFullyPaid ? 'Bill marked as fully paid.' : 'Bill marked as not fully paid.');
                              setError('');
                            } catch (updateError) {
                              setError(updateError.message);
                            }
                          })();
                        }}
                      />
                      <span>Payment done</span>
                    </label>
                  </div>
                  <div className="admin-bill-card__actions">
                    <button
                      type="button"
                      className="admin-form__ghost"
                      onClick={() => setSelectedBill(bill)}
                    >
                      👁️ View Details
                    </button>
                    <button
                      type="button"
                      className="admin-form__ghost"
                      onClick={() => handlePrintBill(bill)}
                    >
                      🖨️ Print
                    </button>
                    <button
                      type="button"
                      className="admin-form__ghost admin-form__ghost--danger"
                      onClick={() => handleDeleteBill(bill.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {selectedBill ? (
            <div className="admin-bill-detail-modal">
              <div className="admin-bill-detail-modal__panel">
                <div className="admin-bill-detail-modal__header">
                  <h2>Bill #{selectedBill.id} - Details</h2>
                  <button
                    type="button"
                    className="admin-form__ghost"
                    onClick={() => setSelectedBill(null)}
                  >
                    ✕ Close
                  </button>
                </div>
                <div className="admin-bill-detail-modal__content">
                  <div className="admin-bill-detail__section">
                    <h3>Customer Information</h3>
                    <div className="admin-bill-detail__info-grid">
                      <div>
                        <strong>Customer Name:</strong>
                        <span>{selectedBill.customerName || 'N/A'}</span>
                      </div>
                      <div>
                        <strong>Contact Number:</strong>
                        <span>{selectedBill.contactNumber || 'N/A'}</span>
                      </div>
                      <div>
                        <strong>Billing Date:</strong>
                        <span>{selectedBill.billingDate || 'N/A'}</span>
                      </div>
                      {selectedBill.billingTime && (
                        <div>
                          <strong>Billing Time:</strong>
                          <span>{selectedBill.billingTime}</span>
                        </div>
                      )}
                      <div>
                        <strong>Billing Mode:</strong>
                        <span>{selectedBill.billingMode || 'N/A'}</span>
                      </div>
                      <div>
                        <strong>Payment Method:</strong>
                        <span>{selectedBill.paymentMethod || 'N/A'}</span>
                      </div>
                      {selectedBill.billedBy && (
                        <div>
                          <strong>Billed By:</strong>
                          <span>{selectedBill.billedBy}</span>
                        </div>
                      )}
                      {selectedBill.shopName && (
                        <div>
                          <strong>Shop Name:</strong>
                          <span>{selectedBill.shopName}</span>
                        </div>
                      )}
                      {selectedBill.panNumber && (
                        <div>
                          <strong>PAN Number:</strong>
                          <span>{selectedBill.panNumber}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="admin-bill-detail__section">
                    <h3>Bill Items</h3>
                    <div className="admin-bill-detail__items-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBill.items?.map((item, index) => (
                            <tr key={index}>
                              <td>{item.productName || 'Item'}</td>
                              <td>{item.quantity}</td>
                              <td>{formatNpr(item.unitPrice)}</td>
                              <td>{formatNpr(item.lineTotal || (item.unitPrice * item.quantity))}</td>
                            </tr>
                          )) || (
                            <tr>
                              <td colSpan="4">No items found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="admin-bill-detail__section">
                    <h3>Bill Summary</h3>
                    <div className="admin-bill-detail__summary">
                      <div>
                        <strong>Subtotal:</strong>
                        <span>{formatNpr(selectedBill.subtotal || 0)}</span>
                      </div>
                      {selectedBill.totalDiscount > 0 && (
                        <div>
                          <strong>Discount:</strong>
                          <span>- {formatNpr(selectedBill.totalDiscount)}</span>
                        </div>
                      )}
                      <div className="admin-bill-detail__total">
                        <strong>Grand Total:</strong>
                        <span>{formatNpr(selectedBill.grandTotal || 0)}</span>
                      </div>
                      <div>
                        <strong>Total Paid:</strong>
                        <span>{formatNpr(selectedBill.paidAmount || 0)}</span>
                      </div>
                      {(selectedBill.onlinePaidAmount > 0 || selectedBill.cashPaidAmount > 0) && (
                        <div style={{ marginLeft: '16px', fontSize: '0.9em', color: '#666' }}>
                          {selectedBill.onlinePaidAmount > 0 && (
                            <div>
                              <strong>🌐 Online:</strong>
                              <span>{formatNpr(selectedBill.onlinePaidAmount)}</span>
                            </div>
                          )}
                          {selectedBill.cashPaidAmount > 0 && (
                            <div>
                              <strong>💵 Cash:</strong>
                              <span>{formatNpr(selectedBill.cashPaidAmount)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div>
                        <strong>Remaining:</strong>
                        <span>{formatNpr(selectedBill.remainingAmount || 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="admin-bill-detail-modal__actions">
                  <button
                    type="button"
                    className="admin-form__submit"
                    onClick={() => handlePrintBill(selectedBill)}
                  >
                    🖨️ Print Bill
                  </button>
                  <button
                    type="button"
                    className="admin-form__cancel"
                    onClick={() => setSelectedBill(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {activeModal === 'view-orders' ? (
            <div className="admin-orders-list">
              {orders.map((order) => (
                <div key={order.id} className="admin-order-card">
                  <div className="admin-order-card__header">
                    <div className="admin-order-card__id">
                      <strong>Order #{order.orderNumber || order.id}</strong>
                      <span className="admin-order-card__date">{new Date(order.createdAt).toLocaleDateString()}</span>
                    </div>
                    <span className={`admin-order-card__status admin-order-card__status--${order.status.toLowerCase()}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="admin-order-card__content">
                    <div className="admin-order-card__info">
                      <div>
                        <strong>Customer</strong>
                        <span>{getOrderCustomerName(order)}</span>
                      </div>
                      <div>
                        <strong>Phone</strong>
                        <span>{getOrderCustomerPhone(order)}</span>
                      </div>
                      {getOrderCustomerEmail(order) ? (
                        <div>
                          <strong>Email</strong>
                          <span>{getOrderCustomerEmail(order)}</span>
                        </div>
                      ) : null}
                      {order.shippingAddress && (
                        <>
                          <div>
                            <strong>Delivery Address</strong>
                            <span>{typeof order.shippingAddress === 'string' ? order.shippingAddress : formatOrderLocation(order) || 'N/A'}</span>
                          </div>
                          {typeof order.shippingAddress !== 'string' && order.shippingAddress.locationLabel ? (
                            <div>
                              <strong>Map Location</strong>
                              <span>{order.shippingAddress.locationLabel}</span>
                            </div>
                          ) : null}
                        </>
                      )}
                      {order.notes ? (
                        <div>
                          <strong>Delivery Notes</strong>
                          <span>{order.notes}</span>
                        </div>
                      ) : null}
                      {order.paymentMethod && (
                        <div>
                          <strong>Payment</strong>
                          <span>{order.paymentMethod}</span>
                        </div>
                      )}
                    </div>
                    <div className="admin-order-card__items">
                      <strong>Items</strong>
                      <span>{formatOrderItems(order)}</span>
                    </div>
                    <div className="admin-order-card__total">
                      <strong>Total</strong>
                      <span>{formatNpr(order.total)}</span>
                    </div>
                  </div>
                  <div className="admin-order-card__actions">
                    <button
                      type="button"
                      className="admin-form__ghost"
                      onClick={() => handleUpdateOrderStatus(order.id)}
                    >
                      📝 Update Status
                    </button>
                    <button
                      type="button"
                      className="admin-form__ghost admin-form__ghost--danger"
                      onClick={() => handleDeleteOrder(order)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {activeModal === 'view-appointments' ? (
            <div className="admin-appointments-list">
              <div className="admin-form__section">
                <div className="admin-form__section-title">Filter Appointments</div>
                <div className="admin-form__row">
                  <label className="admin-form__field">
                    <span>Filter by Date</span>
                    <input
                      type="date"
                      value={appointmentDate}
                      onChange={(event) => {
                        setAppointmentDate(event.target.value);
                        void loadAppointmentsByDate(event.target.value);
                      }}
                    />
                  </label>
                </div>
              </div>
              {appointments.map((appointment) => (
                <div key={appointment.id} className="admin-appointment-card">
                  <div className="admin-appointment-card__header">
                    <div className="admin-appointment-card__id">
                      <strong>Appointment #{appointment.id}</strong>
                      <span className="admin-appointment-card__date">{appointment.date}</span>
                    </div>
                    <span className="admin-appointment-card__time">
                      🕐 {appointment.time}
                    </span>
                  </div>
                  <div className="admin-appointment-card__content">
                    <div className="admin-appointment-card__info">
                      <div>
                        <strong>Customer:</strong>
                        <span>{appointment.customerName}</span>
                      </div>
                      <div>
                        <strong>Contact:</strong>
                        <span>{appointment.contactNumber}</span>
                      </div>
                      {appointment.service && (
                        <div>
                          <strong>Service:</strong>
                          <span>{appointment.service}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="admin-appointment-card__actions">
                    <button
                      type="button"
                      className="admin-form__ghost"
                      onClick={() => handleRescheduleAppointment(appointment.id)}
                    >
                      📅 Reschedule
                    </button>
                    <button
                      type="button"
                      className="admin-form__ghost admin-form__ghost--danger"
                      onClick={() => handleCancelAppointment(appointment.id)}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {activeModal === 'promo-codes' ? (
            <div className="admin-promo-container">
              <form className="admin-form admin-form--inline" onSubmit={handlePromoSubmit}>
                <div className="admin-form__row">
                  <label className="admin-form__field">
                    <span>Promo Code</span>
                    <input
                      required
                      type="text"
                      placeholder="Enter code"
                      value={promoForm.code}
                      onChange={(event) => setPromoForm((current) => ({ ...current, code: event.target.value }))}
                    />
                  </label>
                  <label className="admin-form__field">
                    <span>Discount %</span>
                    <input
                      required
                      type="number"
                      min="1"
                      max="100"
                      placeholder="Enter percentage"
                      value={promoForm.percentOff}
                      onChange={(event) => setPromoForm((current) => ({ ...current, percentOff: event.target.value }))}
                    />
                  </label>
                </div>
                <button type="submit" className="admin-form__submit" disabled={isSaving}>
                  {isSaving ? 'Creating...' : 'Create Promo Code'}
                </button>
              </form>

              <div className="admin-promo-list">
                {promoCodes.map((promo) => (
                  <div key={promo.id} className="admin-promo-card">
                    <div className="admin-promo-card__code">
                      <strong>{promo.code}</strong>
                    </div>
                    <div className="admin-promo-card__discount">
                      <span>{promo.percentOff}% OFF</span>
                    </div>
                    <button
                      type="button"
                      className="admin-form__ghost admin-form__ghost--danger"
                      onClick={() => void handlePromoDelete(promo.id)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {activeModal === 'sales-records' ? (
            <div className="admin-billing-container">
              <div className="admin-billing-form">
                <h3 className="admin-form__section-title">Sales Filters</h3>
                <div className="admin-form__row">
                  <label className="admin-form__field">
                    <span>Period</span>
                    <select
                      value={salesPeriod}
                      onChange={(event) => {
                        const nextPeriod = event.target.value === 'month' ? 'month' : 'day';
                        setSalesPeriod(nextPeriod);
                        void loadSalesRecords({
                          period: nextPeriod,
                          day: salesDay,
                          month: salesMonth,
                        });
                      }}
                    >
                      <option value="day">Day</option>
                      <option value="month">Month</option>
                    </select>
                  </label>
                  {salesPeriod === 'month' ? (
                    <label className="admin-form__field">
                      <span>Month</span>
                      <input
                        type="month"
                        value={salesMonth}
                        onChange={(event) => {
                          setSalesMonth(event.target.value);
                          void loadSalesRecords({
                            period: 'month',
                            day: salesDay,
                            month: event.target.value,
                          });
                        }}
                      />
                    </label>
                  ) : (
                    <label className="admin-form__field">
                      <span>Day</span>
                      <input
                        type="date"
                        value={salesDay}
                        onChange={(event) => {
                          setSalesDay(event.target.value);
                          void loadSalesRecords({
                            period: 'day',
                            day: event.target.value,
                            month: salesMonth,
                          });
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {salesRecords ? (
                <div className="admin-billing-summary">
                  <div className="admin-billing-summary__row">
                    <span>Period:</span>
                    <strong>
                      {salesRecords.period} ({salesRecords.label})
                    </strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Total Bills:</span>
                    <strong>{salesRecords.totals?.totalBills || 0}</strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Total Sales:</span>
                    <strong>{formatNpr(salesRecords.totals?.totalSales || 0)}</strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Total Expenses:</span>
                    <strong>{formatNpr(salesRecords.totals?.totalExpenses || 0)}</strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Net Sales:</span>
                    <strong>{formatNpr(salesRecords.totals?.netSales || 0)}</strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Total Paid:</span>
                    <strong>{formatNpr(salesRecords.totals?.totalPaid || 0)}</strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Total Remaining:</span>
                    <strong>{formatNpr(salesRecords.totals?.totalRemaining || 0)}</strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Cash Sales:</span>
                    <strong>{formatNpr(salesRecords.paymentBreakdown?.cash?.sales || 0)}</strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Online Sales:</span>
                    <strong>{formatNpr(salesRecords.paymentBreakdown?.online?.sales || 0)}</strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span><strong>Billing Mode Breakdown</strong></span>
                    <strong />
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Online Mode — Sales / Paid / Remaining:</span>
                    <strong>
                      {formatNpr(salesRecords.modeBreakdown?.online?.sales || 0)} /{' '}
                      {formatNpr(salesRecords.modeBreakdown?.online?.paid || 0)} /{' '}
                      {formatNpr(salesRecords.modeBreakdown?.online?.remaining || 0)}
                    </strong>
                  </div>
                  <div className="admin-billing-summary__row">
                    <span>Offline Mode — Sales / Paid / Remaining:</span>
                    <strong>
                      {formatNpr(salesRecords.modeBreakdown?.offline?.sales || 0)} /{' '}
                      {formatNpr(salesRecords.modeBreakdown?.offline?.paid || 0)} /{' '}
                      {formatNpr(salesRecords.modeBreakdown?.offline?.remaining || 0)}
                    </strong>
                  </div>
                </div>
              ) : (
                <p className="admin-loading">Loading sales records...</p>
              )}

              {salesRecords ? (
                <div className="admin-sales-charts">
                  {(() => {
                    const cashSales = Number(salesRecords.paymentBreakdown?.cash?.sales || 0);
                    const onlineSales = Number(salesRecords.paymentBreakdown?.online?.sales || 0);
                    const totalSales = Number(salesRecords.totals?.totalSales || 0);
                    const totalExpenses = Number(salesRecords.totals?.totalExpenses || 0);
                    const netSales = Number(salesRecords.totals?.netSales || 0);
                    const salesTotalForPie = Math.max(0, cashSales + onlineSales);
                    const cashPercent = salesTotalForPie > 0 ? (cashSales / salesTotalForPie) * 100 : 0;
                    const onlinePercent = salesTotalForPie > 0 ? (onlineSales / salesTotalForPie) * 100 : 0;
                    const maxBarValue = Math.max(totalSales, totalExpenses, Math.abs(netSales), 1);
                    const netIsNegative = netSales < 0;
                    const billedByRows = ['Satya Sharma', 'Kushal Dhungel']
                      .map((name) => {
                        const person = (salesRecords.billedByBreakdown || []).find((item) => item.name === name);
                        return {
                          name,
                          sales: Number(person?.sales || 0),
                        };
                      })
                      .concat(
                        (salesRecords.billedByBreakdown || [])
                          .filter((person) => !['Satya Sharma', 'Kushal Dhungel'].includes(person.name))
                          .map((person) => ({
                            name: person.name,
                            sales: Number(person.sales || 0),
                          }))
                      );

                    return (
                      <>
                        <div className="admin-sales-chart-card">
                          <div className="admin-sales-chart-card__header">
                            <strong>Cash vs Online</strong>
                            <span className="admin-sales-chart-card__meta">
                              {salesRecords.period} {salesRecords.label}
                            </span>
                          </div>
                          <div className="admin-sales-pie">
                            <div
                              className="admin-sales-pie__disc"
                              style={{
                                background:
                                  salesTotalForPie > 0
                                    ? `conic-gradient(#1e3a8a 0 ${cashPercent}%, #10b981 ${cashPercent}% 100%)`
                                    : 'conic-gradient(#e2e8f0 0 100%)',
                              }}
                              aria-label="Cash vs Online pie chart"
                            />
                            <div className="admin-sales-pie__legend">
                              <div className="admin-sales-legend-row">
                                <span className="admin-sales-legend-swatch admin-sales-legend-swatch--cash" />
                                <span>Cash</span>
                                <strong>
                                  {formatNpr(cashSales)} ({cashPercent.toFixed(0)}%)
                                </strong>
                              </div>
                              <div className="admin-sales-legend-row">
                                <span className="admin-sales-legend-swatch admin-sales-legend-swatch--online" />
                                <span>Online</span>
                                <strong>
                                  {formatNpr(onlineSales)} ({onlinePercent.toFixed(0)}%)
                                </strong>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="admin-sales-chart-card">
                          <div className="admin-sales-chart-card__header">
                            <strong>Online Mode vs Offline Mode</strong>
                            <span className="admin-sales-chart-card__meta">Billing mode</span>
                          </div>
                          {(() => {
                            const onlineModeSales = Number(salesRecords.modeBreakdown?.online?.sales || 0);
                            const offlineModeSales = Number(salesRecords.modeBreakdown?.offline?.sales || 0);
                            const totalModeSales = Math.max(0, onlineModeSales + offlineModeSales);
                            const onlineModePercent = totalModeSales > 0 ? (onlineModeSales / totalModeSales) * 100 : 0;
                            const offlineModePercent = totalModeSales > 0 ? (offlineModeSales / totalModeSales) * 100 : 0;

                            return (
                              <div className="admin-sales-pie">
                                <div
                                  className="admin-sales-pie__disc"
                                  style={{
                                    background:
                                      totalModeSales > 0
                                        ? `conic-gradient(#0ea5e9 0 ${onlineModePercent}%, #f97316 ${onlineModePercent}% 100%)`
                                        : 'conic-gradient(#e2e8f0 0 100%)',
                                  }}
                                  aria-label="Online mode vs Offline mode pie chart"
                                />
                                <div className="admin-sales-pie__legend">
                                  <div className="admin-sales-legend-row">
                                    <span
                                      className="admin-sales-legend-swatch"
                                      style={{ background: '#0ea5e9' }}
                                    />
                                    <span>Online mode</span>
                                    <strong>
                                      {formatNpr(onlineModeSales)} ({onlineModePercent.toFixed(0)}%)
                                    </strong>
                                  </div>
                                  <div className="admin-sales-legend-row">
                                    <span
                                      className="admin-sales-legend-swatch"
                                      style={{ background: '#f97316' }}
                                    />
                                    <span>Offline mode</span>
                                    <strong>
                                      {formatNpr(offlineModeSales)} ({offlineModePercent.toFixed(0)}%)
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        <div className="admin-sales-chart-card">
                          <div className="admin-sales-chart-card__header">
                            <strong>Sales vs Expenses vs Net</strong>
                            <span className="admin-sales-chart-card__meta">Totals</span>
                          </div>
                          <div className="admin-sales-bars">
                            <div className="admin-sales-bar">
                              <div className="admin-sales-bar__label">
                                <span>Sales</span>
                                <strong>{formatNpr(totalSales)}</strong>
                              </div>
                              <div className="admin-sales-bar__track">
                                <div
                                  className="admin-sales-bar__fill admin-sales-bar__fill--sales"
                                  style={{ width: `${Math.min(100, (totalSales / maxBarValue) * 100)}%` }}
                                />
                              </div>
                            </div>

                            <div className="admin-sales-bar">
                              <div className="admin-sales-bar__label">
                                <span>Expenses</span>
                                <strong>{formatNpr(totalExpenses)}</strong>
                              </div>
                              <div className="admin-sales-bar__track">
                                <div
                                  className="admin-sales-bar__fill admin-sales-bar__fill--expenses"
                                  style={{ width: `${Math.min(100, (totalExpenses / maxBarValue) * 100)}%` }}
                                />
                              </div>
                            </div>

                            <div className="admin-sales-bar">
                              <div className="admin-sales-bar__label">
                                <span>Net</span>
                                <strong className={netIsNegative ? 'admin-sales-text--danger' : 'admin-sales-text--success'}>
                                  {formatNpr(netSales)}
                                </strong>
                              </div>
                              <div className="admin-sales-bar__track">
                                <div
                                  className={`admin-sales-bar__fill ${netIsNegative ? 'admin-sales-bar__fill--danger' : 'admin-sales-bar__fill--success'}`}
                                  style={{ width: `${Math.min(100, (Math.abs(netSales) / maxBarValue) * 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="admin-sales-chart-card admin-sales-billed-by">
                          <div className="admin-sales-chart-card__header">
                            <strong>Total Sales By Billed By</strong>
                            <span className="admin-sales-chart-card__meta">
                              {salesRecords.period} {salesRecords.label}
                            </span>
                          </div>
                          <div className="admin-sales-billed-by__rows">
                            {billedByRows.map((person) => (
                              <div className="admin-sales-billed-by__row" key={person.name}>
                                <span>{person.name}</span>
                                <strong>{formatNpr(person.sales)}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : null}
            </div>
          ) : null}

          {activeModal === 'expenses' ? (
            <div className="admin-billing-container">
              <div className="admin-billing-form">
                <h3 className="admin-form__section-title">Log Expense</h3>
                <form className="admin-form" onSubmit={handleCreateExpense}>
                  <div className="admin-form__row">
                    <label className="admin-form__field">
                      <span>Expense Day</span>
                      <input
                        type="date"
                        value={expenseDay}
                        onChange={(event) => {
                          setExpenseDay(event.target.value);
                          void loadExpenses(event.target.value);
                        }}
                      />
                    </label>
                    <label className="admin-form__field">
                      <span>Amount</span>
                      <input
                        required
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={expenseForm.amount}
                        onChange={(event) => setExpenseForm((current) => ({ ...current, amount: event.target.value }))}
                      />
                    </label>
                  </div>
                  <label className="admin-form__field">
                    <span>Message</span>
                    <input
                      required
                      type="text"
                      placeholder="e.g. courier, rent, packaging, electricity"
                      value={expenseForm.message}
                      onChange={(event) => setExpenseForm((current) => ({ ...current, message: event.target.value }))}
                    />
                  </label>
                  <div className="admin-form__actions">
                    <button type="submit" className="admin-form__submit" disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Expense'}
                    </button>
                  </div>
                </form>
              </div>

              <div className="admin-billing-summary">
                <div className="admin-billing-summary__row">
                  <span>Total expenses ({expenseDay}):</span>
                  <strong>{formatNpr(expenseTotal)}</strong>
                </div>
              </div>

              <div className="admin-bills-list">
                {expenses.map((expense) => (
                  <div key={expense.id} className="admin-bill-card">
                    <div className="admin-bill-card__header">
                      <strong>{formatNpr(expense.amount)}</strong>
                      <span>{expense.expenseDate}</span>
                    </div>
                    <div className="admin-bill-card__content">
                      <p><strong>Message:</strong> {expense.message}</p>
                    </div>
                    <div className="admin-bill-card__actions">
                      <button
                        type="button"
                        className="admin-form__ghost admin-form__ghost--danger"
                        onClick={() => handleDeleteExpense(expense.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {!expenses.length ? <p className="admin-loading">No expenses for this day.</p> : null}
              </div>
            </div>
          ) : null}

          {activeModal === 'contact-messages' ? (
            <div className="admin-messages-list">
              {contactMessages.map((message) => (
                <div key={message.id} className="admin-message-card">
                  <div className="admin-message-card__header">
                    <strong>{message.name}</strong>
                    <span>{new Date(message.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="admin-message-card__content">
                    <p><strong>Email:</strong> {message.email}</p>
                    <p><strong>Phone:</strong> {message.phone}</p>
                    <p><strong>Message:</strong> {message.message}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {activeModal === 'view-users' ? (
            <div className="admin-users-view">
              <div className="admin-users-summary">
                <div>
                  <span>Total Users</span>
                  <strong>{users.length || summary?.metrics.userCount || 0}</strong>
                </div>
                <div>
                  <span>Admins</span>
                  <strong>{users.filter((customer) => customer.role === 'admin').length}</strong>
                </div>
                <div>
                  <span>Customers</span>
                  <strong>{users.filter((customer) => customer.role !== 'admin').length}</strong>
                </div>
              </div>

              <div className="admin-users-list">
                {users.map((customer) => (
                  <div key={customer.id} className="admin-user-card">
                    <div className="admin-user-card__header">
                      <div className="admin-user-card__avatar">
                        {(customer.fullName || customer.email || 'U').slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <strong>{customer.fullName || 'Unnamed User'}</strong>
                        <span>{customer.email || 'No email'}</span>
                      </div>
                      <span className={`admin-record-badge ${customer.role === 'admin' ? 'admin-record-badge--paid' : 'admin-record-badge--due'}`}>
                        {customer.role}
                      </span>
                    </div>
                    <div className="admin-user-card__content">
                      <div className="admin-record-info-grid">
                        <p><strong>Phone</strong><span>{customer.phone || 'N/A'}</span></p>
                        <p><strong>Joined</strong><span>{customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}</span></p>
                        <p><strong>Saved Addresses</strong><span>{customer.addressBook?.length || 0}</span></p>
                        <p><strong>User ID</strong><span>{customer.id}</span></p>
                      </div>
                      {customer.addressBook?.length ? (
                        <div className="admin-user-card__addresses">
                          <strong>Addresses</strong>
                          {customer.addressBook.map((address, index) => (
                            <p key={`${customer.id}-address-${index}`}>
                              {address.label ? `${address.label}: ` : ''}
                              {address.addressLine || address.addressLine1 || address.locationLabel || 'Saved address'}
                            </p>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="admin-user-card__actions">
                      <button
                        type="button"
                        className="admin-form__ghost admin-form__ghost--danger"
                        onClick={() => handleDeleteUser(customer)}
                        disabled={customer.id === user.id}
                        title={customer.id === user.id ? 'You cannot delete your own admin account.' : undefined}
                      >
                        Delete User
                      </button>
                    </div>
                  </div>
                ))}
                {!users.length ? (
                  <p className="admin-loading">No users found.</p>
                ) : null}
              </div>
            </div>
          ) : null}

          {activeModal === 'migrate-bills' ? (
            <div className="admin-migrate-container">
              <div className="admin-form__section">
                <h2>Migrate Bill Payments</h2>
                <p>This will fix payment tracking for existing bills that don't have proper cash/online payment breakdown.</p>
                <div className="admin-form__section" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <h3>What this does:</h3>
                  <ul style={{ marginLeft: '20px', color: '#666' }}>
                    <li>Finds bills without proper payment method tracking</li>
                    <li>Assigns existing payments to correct payment type (cash/online)</li>
                    <li>Enables accurate payment breakdown display</li>
                  </ul>
                </div>
                <div className="admin-form__actions" style={{ marginTop: '20px' }}>
                  <button
                    type="button"
                    className="admin-form__submit"
                    onClick={handleMigrateBills}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Migrating...' : '🔄 Migrate Bills'}
                  </button>
                  <button
                    type="button"
                    className="admin-form__cancel"
                    onClick={() => setActiveModal('')}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {confirmDialog.show ? (
            <div className="admin-confirm-dialog">
              <div className="admin-confirm-dialog__panel">
                <div className="admin-confirm-dialog__content">
                  <h3>Confirm Action</h3>
                  <p>{confirmDialog.message}</p>
                </div>
                <div className="admin-confirm-dialog__actions">
                  <button
                    type="button"
                    className="admin-form__cancel"
                    onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null })}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="admin-form__submit"
                    onClick={confirmDialog.onConfirm}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

export default AdminDashboardPage;
